import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { fetchPatternByFileName } from './library.service';
import { FIREBASE_STORAGE_TEMP_IMAGES_PATH } from '../config/constants';

// Fetches all patterns a user has ever generated, ordered newest first.
// Library patterns are enriched with their display name from the library collection.
export const fetchMyPatterns = async (uid) => {
	const col = collection(db, 'customers', uid, 'purchasedPatterns');
	const q = query(col, orderBy('purchasedAt', 'desc'));
	const snap = await getDocs(q);
	const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

	// Resolve library pattern names in parallel (deduplicated by fileName)
	const libraryFileNames = [
		...new Set(
			docs
				.filter((d) => d.type === 'library' && d.fileName)
				.map((d) => d.fileName),
		),
	];
	const nameMap = {};
	await Promise.all(
		libraryFileNames.map(async (fileName) => {
			const lib = await fetchPatternByFileName(fileName);
			if (lib?.name) nameMap[fileName] = lib.name;
		}),
	);

	// Try to fetch temp image URLs for user patterns in parallel.
	// getDownloadURL throws if the file has already been deleted — that's fine.
	const tempImageMap = {};
	await Promise.all(
		docs
			.filter((d) => d.type === 'user' && d.patternId)
			.map(async (d) => {
				try {
					const storageRef = ref(
						storage,
						`${FIREBASE_STORAGE_TEMP_IMAGES_PATH}/${uid}/${d.patternId}.jpg`,
					);
					tempImageMap[d.patternId] =
						await getDownloadURL(storageRef);
				} catch {
					// File deleted or not accessible — fall back to placeholder
				}
			}),
	);

	return docs.map((d) => {
		if (d.type === 'library' && d.fileName && nameMap[d.fileName]) {
			return { ...d, libraryName: nameMap[d.fileName] };
		}
		if (d.type === 'user' && d.patternId && tempImageMap[d.patternId]) {
			return { ...d, tempImageURL: tempImageMap[d.patternId] };
		}
		return d;
	});
};
